import { memo, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Crown, TrendingUp, TrendingDown, Flame, Volume2, VolumeX } from "lucide-react";
import { CRYPTO_WIN_THRESHOLD } from "@/game/constants";
import PlayerNameLink from "@/components/PlayerNameLink";

/**
 * Live scoreboard with competitive-tension cues:
 *  • per-player progress bar to $1,250 crypto-win threshold
 *  • score gap from the leader (−$X behind / LEADER)
 *  • momentum arrow (▲/▼) based on prior crypto_value
 *  • "Close to win!" banner when any player is within $200 of winning
 *  • per-opponent reaction-mute toggle (KNOWN_ISSUES §1.32) — pass
 *    `mute={{ isMuted, toggleMute }}` from useReactionMute(gameId)
 */
function LiveScoreboard({ game, aiAction, mute }) {
  const { t } = useTranslation();
  // Track previous crypto_value per player to show momentum.
  const prevValuesRef = useRef({});
  // Briefly flash the arrow the first time it changes.
  const [flashing, setFlashing] = useState({});

  useEffect(() => {
    const next = {};
    const nowFlash = {};
    (game.players || []).forEach((p, i) => {
      const prev = prevValuesRef.current[i];
      if (typeof prev === "number" && prev !== p.crypto_value) {
        nowFlash[i] = p.crypto_value > prev ? "up" : "down";
      }
      next[i] = p.crypto_value;
    });
    prevValuesRef.current = next;
    if (Object.keys(nowFlash).length) {
      setFlashing(nowFlash);
      // `flashTimer`, NOT `t` — `useTranslation`'s `t` is in scope here
      // and shadowing breaks any future t("...") inside this block.
      const flashTimer = window.setTimeout(() => setFlashing({}), 1400);
      return () => window.clearTimeout(flashTimer);
    }
  }, [game.players]);

  const ranked = [...game.players]
    .map((p, i) => ({ ...p, idx: i }))
    .sort((a, b) => b.crypto_value - a.crypto_value);

  const topValue = ranked[0]?.crypto_value || 0;
  const closeToWin = topValue >= CRYPTO_WIN_THRESHOLD - 200 && topValue < CRYPTO_WIN_THRESHOLD;
  const remainingToWin = Math.max(0, CRYPTO_WIN_THRESHOLD - topValue);

  // Which player index is currently acting per the AI banner (if any)?
  const actingIdx = aiAction?.botName
    ? (game.players || []).findIndex((p) => p.name === aiAction.botName)
    : -1;
  const actingTone =
    aiAction?.type === "buy"
      ? "ring-2 ring-[#00E676] ring-offset-1 ring-offset-white shadow-[0_0_14px_rgba(0,230,118,0.55)]"
      : aiAction?.type === "sell"
      ? "ring-2 ring-[#FF5A5F] ring-offset-1 ring-offset-white shadow-[0_0_14px_rgba(255,90,95,0.55)]"
      : aiAction?.type === "hold"
      ? "ring-2 ring-[#FFC107] ring-offset-1 ring-offset-white shadow-[0_0_14px_rgba(255,193,7,0.55)]"
      : "ring-2 ring-[#38B6FF] ring-offset-1 ring-offset-white shadow-[0_0_14px_rgba(56,182,255,0.55)]";

  return (
    <div
      className="bg-white/70 dark:bg-slate-900/70 rounded-2xl border border-[#1A237E]/10 dark:border-[#E3F2FD]/10 p-3 mt-4 w-full max-w-md"
      data-testid="live-scoreboard"
    >
      <div className="flex items-center justify-between mb-2">
        <h3
          className="text-xs font-bold text-[#1A237E] dark:text-[#E3F2FD] flex items-center gap-1"
          style={{ fontFamily: "Fredoka" }}
        >
          <Crown className="w-3.5 h-3.5 text-[#FFC107]" /> {t("liveScoreboard.title")}
        </h3>
        {closeToWin && (
          <span
            data-testid="close-to-win-alert"
            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-red-500 text-white rounded-full px-2 py-0.5 animate-urgency-blink"
          >
            <Flame className="w-3 h-3" /> {t("liveScoreboard.label.toWin", { value: remainingToWin })}
          </span>
        )}
      </div>
      <div className="space-y-1.5">
        {ranked.map((p, rank) => {
          const isLeader = rank === 0;
          const gap = topValue - p.crypto_value;
          const pct = Math.min(100, Math.round((p.crypto_value / CRYPTO_WIN_THRESHOLD) * 100));
          const momentum = flashing[p.idx];
          const isActing = actingIdx === p.idx;
          return (
            <div
              key={p.idx}
              className={`rounded-lg text-xs transition-all duration-300 ${
                isActing
                  ? `bg-white/90 dark:bg-slate-900/90 ${actingTone} animate-bounce-in`
                  : p.idx === game.current_player_index
                  ? "bg-[#38B6FF]/10 border border-[#38B6FF]/30"
                  : "bg-white/50 dark:bg-slate-900/50 border border-transparent"
              }`}
              data-testid={`scoreboard-player-${p.idx}`}
            >
              <div className="flex items-center gap-2 px-2 py-1.5">
                <span className={`font-bold w-4 ${isLeader ? "text-[#FFC107]" : "text-[#1A237E]/40 dark:text-[#E3F2FD]/40"}`}>
                  {rank + 1}
                </span>
                <span className="font-bold text-[#1A237E] dark:text-[#E3F2FD] flex-1 truncate flex items-center gap-1">
                  {/* user_id_map is keyed by stringified player index.
                      Pass-and-play / bot seats won't have an entry —
                      PlayerNameLink falls through to plain text in
                      that case so we don't need a guard here. */}
                  <PlayerNameLink
                    userId={game.user_id_map?.[String(p.idx)]}
                    name={p.name}
                    className="font-bold truncate"
                    testId={`scoreboard-name-${p.idx}`}
                  />
                  {isActing && aiAction?.symbol && (
                    <span
                      className="ml-1 inline-flex items-center rounded-full bg-[#1A237E]/90 dark:bg-[#E3F2FD]/90 text-white text-[9px] font-bold px-1.5 py-0.5 animate-bounce-in"
                      data-testid={`acting-symbol-${p.idx}`}
                      style={{ fontFamily: "Fredoka" }}
                    >
                      {aiAction.verb} {aiAction.symbol}
                    </span>
                  )}
                  {momentum === "up" && (
                    <TrendingUp
                      className="w-3 h-3 text-green-500 animate-bounce-in"
                      data-testid={`momentum-up-${p.idx}`}
                    />
                  )}
                  {momentum === "down" && (
                    <TrendingDown
                      className="w-3 h-3 text-red-500 animate-bounce-in"
                      data-testid={`momentum-down-${p.idx}`}
                    />
                  )}
                </span>
                <span className="font-bold text-[#38B6FF] tabular-nums">${p.crypto_value}</span>
                {isLeader ? (
                  <span className="text-[9px] font-bold uppercase tracking-wider text-[#FFC107]">
                    {t("liveScoreboard.label.lead")}
                  </span>
                ) : (
                  <span
                    className="text-[10px] font-semibold text-[#FF5A5F] tabular-nums"
                    data-testid={`gap-${p.idx}`}
                  >
                    −${gap}
                  </span>
                )}
                {p.skip_next_turn && (
                  <Badge variant="destructive" className="text-[8px] h-4 px-1">
                    {t("liveScoreboard.label.skip")}
                  </Badge>
                )}
                {mute && (
                  <button
                    type="button"
                    onClick={() => mute.toggleMute(p.idx)}
                    className="ml-1 p-0.5 rounded hover:bg-[#1A237E]/10 dark:bg-[#E3F2FD]/10 transition-colors"
                    title={mute.isMuted(p.idx)
                      ? t("liveScoreboard.label.unmuteReactions", { name: p.name })
                      : t("liveScoreboard.label.muteReactions", { name: p.name })}
                    data-testid={`mute-toggle-${p.idx}`}
                  >
                    {mute.isMuted(p.idx)
                      ? <VolumeX className="w-3 h-3 text-[#FF5A5F]" />
                      : <Volume2 className="w-3 h-3 text-[#1A237E]/40 dark:text-[#E3F2FD]/40" />}
                  </button>
                )}
              </div>
              {/* Win-progress bar */}
              <div className="px-2 pb-1.5">
                <div className="h-1 w-full rounded-full bg-[#1A237E]/10 dark:bg-[#E3F2FD]/10 overflow-hidden">
                  <div
                    className={`h-full transition-all duration-700 ease-out ${
                      isLeader ? "bg-[#FFC107]" : "bg-[#38B6FF]"
                    }`}
                    style={{ width: `${pct}%` }}
                    data-testid={`win-progress-${p.idx}`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 pt-2 border-t border-[#1A237E]/10 dark:border-[#E3F2FD]/10 text-[10px] text-center text-[#1A237E]/40 dark:text-[#E3F2FD]/40">
        {t("liveScoreboard.footer", { value: CRYPTO_WIN_THRESHOLD.toLocaleString() })}
      </div>
    </div>
  );
}

export default memo(LiveScoreboard);
